import React, { useState, useEffect, useMemo } from 'react';
import { 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  Button, 
  Typography, 
  Box, 
  FormControl, 
  InputLabel, 
  Select, 
  MenuItem, 
  CircularProgress,
  TextField,
  Grid,
  Tabs,
  Tab,
  IconButton,
  Tooltip,
  FormControlLabel,
  RadioGroup,
  Radio,
  Alert,
  Switch,
} from '@mui/material';
import BarChartIcon from '@mui/icons-material/BarChart';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CloseIcon from '@mui/icons-material/Close';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, Legend, ResponsiveContainer, LineChart, Line } from 'recharts';
import useApi from '../../hooks/useApi';
import branding from '../../styles/branding';
import {
  parseSurveyDate,
  mergeLineChartTimeIntoQuery,
  parseLineChartTimeFromQuery,
  monthMenuItems,
  aggregateLineChartPreview,
  mergeDateLikeColumnNames,
} from '../../utils/lineChartTime';
import { getApiErrorMessage } from '../../utils/errorMessages';
import { splitMultiValue } from '../../utils/multiValue';
import { useErrorToast } from '../common';
import './BuildBarChart.css';

const AGGREGATION_OPTIONS = [
  { value: 'sum', label: 'Sum' },
  { value: 'count', label: 'Count' },
  { value: 'average', label: 'Average' },
  { value: 'min', label: 'Min' },
  { value: 'max', label: 'Max' },
  { value: 'median', label: 'Median' }
];

function getCellValue(row, columnName) {
  if (row[columnName] !== undefined && row[columnName] !== null) return row[columnName];
  const key = Object.keys(row).find(k => String(k).trim().toLowerCase() === String(columnName).trim().toLowerCase());
  return key !== undefined ? row[key] : undefined;
}

function isNumericValue(val) {
  if (val === null || val === undefined || val === '') return false;
  if (typeof val === 'number') return !isNaN(val);
  const s = String(val).replace(/,/g, '').trim();
  return s !== '' && !isNaN(parseFloat(s));
}

const BuildBarChart = ({ open, onClose, onAddToDashboard, jobId, initialData, editWidgetId, onUpdateCard, lineMode = false }) => {
  const [chartTab, setChartTab] = useState(lineMode ? 'grouped' : 'simple');
  const [chartData, setChartData] = useState([]);
  const [columns, setColumns] = useState([]);
  const [selectedXAxis, setSelectedXAxis] = useState('');
  const [selectedYAxis, setSelectedYAxis] = useState('');
  /** Optional series column for side-by-side grouped bars (e.g. facility × site). */
  const [selectedSeries, setSelectedSeries] = useState('');
  const [groupedSeriesMode, setGroupedSeriesMode] = useState(false);
  const [aggregation, setAggregation] = useState('average');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [processedData, setProcessedData] = useState([]);
  const [stackedSegmentKeys, setStackedSegmentKeys] = useState([]);
  const [widgetName, setWidgetName] = useState('');
  const [dataType, setDataType] = useState('');
  const [showValuesOnBars, setShowValuesOnBars] = useState(false);
  const api = useApi();
  const widgetType = lineMode ? 'linechart' : 'barchart';
  const chartDisplayName = lineMode ? 'Line Chart' : 'Bar Chart';
  const [dateColumns, setDateColumns] = useState([]);
  const [analyzeError, setAnalyzeError] = useState(null);
  const [lineTimeGranularity, setLineTimeGranularity] = useState('day');
  const [lineTimeScope, setLineTimeScope] = useState('all');
  const [lineFilterYear, setLineFilterYear] = useState('');
  const [lineFilterMonth, setLineFilterMonth] = useState('');
  const [lineFilterYearStart, setLineFilterYearStart] = useState('');
  const [lineFilterYearEnd, setLineFilterYearEnd] = useState('');

  const availableYears = useMemo(() => {
    if (!lineMode || !selectedXAxis || !chartData.length) return [];
    const ys = new Set();
    chartData.forEach((row) => {
      const d = parseSurveyDate(getCellValue(row, selectedXAxis));
      if (d) ys.add(d.getFullYear());
    });
    return [...ys].sort((a, b) => a - b);
  }, [lineMode, selectedXAxis, chartData]);
  const { showErrorToast } = useErrorToast();

  useEffect(() => {
    if (error) {
      showErrorToast(error);
      setError(null);
    }
  }, [error, showErrorToast]);

  const isNumericalY = useMemo(() => {
    if (!selectedYAxis || chartData.length === 0) return true;
    const yValues = chartData.map(row => getCellValue(row, selectedYAxis)).filter(val => val !== null && val !== undefined && val !== '');
    if (yValues.length === 0) return true;
    const numericalCount = yValues.filter(val => isNumericValue(val)).length;
    return numericalCount > 0;
  }, [selectedYAxis, chartData]);

  const isNumericalX = useMemo(() => {
    if (!selectedXAxis || chartData.length === 0) return true;
    const xValues = chartData.map(row => getCellValue(row, selectedXAxis)).filter(val => val !== null && val !== undefined && val !== '');
    if (xValues.length === 0) return true;
    const numericalCount = xValues.filter(val => isNumericValue(val)).length;
    return numericalCount > 0;
  }, [selectedXAxis, chartData]);

  const isGroupedBothString = chartTab === 'grouped' && !isNumericalX && !isNumericalY;

  const handleYAxisChange = (e) => {
    const newY = e.target.value;
    setSelectedYAxis(newY);
    
    if (chartData.length > 0) {
      const yValues = chartData.map(row => getCellValue(row, newY)).filter(val => val !== null && val !== undefined && val !== '');
      const numericalCount = yValues.filter(val => isNumericValue(val)).length;
      const isNum = yValues.length === 0 || numericalCount > 0;
      setAggregation(isNum ? 'average' : 'count');
    }
  };

  useEffect(() => {
    if (!isNumericalY && aggregation !== 'count') {
      setAggregation('count');
    }
  }, [isNumericalY, aggregation]);

  const chartColors = [
    'rgb(64, 121, 141)',
    'rgb(209, 185, 167)',
    'rgb(149, 198, 213)',
    'rgb(145, 89, 145)',
    'rgb(165, 198, 165)',
    'rgb(125, 111, 100)',
    'rgb(170, 188, 244)',
    'rgb(73, 119, 73)',
    'rgb(208, 181, 208)',
    'rgb(102, 117, 121)'
  ];

  useEffect(() => {
    if (open) {
      let isGrouped = false;
      let parsedQuery = {};
      try {
        if (initialData?.query && initialData.query.startsWith('{')) {
          parsedQuery = JSON.parse(initialData.query);
        }
      } catch (e) {
        // ignore
      }

      if (lineMode) {
        setChartTab('grouped');
        isGrouped = true;
      } else {
        if (parsedQuery.__groupedBar) {
          setChartTab('grouped');
          isGrouped = true;
        } else if (parsedQuery.__simpleBar) {
          setChartTab('simple');
          isGrouped = false;
        } else if (initialData?.selectedColumns?.length > 1) {
          setChartTab('grouped');
          isGrouped = true;
        } else {
          setChartTab('simple');
          isGrouped = false;
        }
      }

      if (initialData?.widgetName) setWidgetName(initialData.widgetName);
      if (initialData?.aggregation) setAggregation(initialData.aggregation);
      
      if (parsedQuery.showValuesOnBars) {
        setShowValuesOnBars(true);
      } else {
        setShowValuesOnBars(false);
      }

      fetchChartData(isGrouped);
    }
  }, [open, lineMode]);

  useEffect(() => {
    if (!open || !lineMode) return;
    const t = parseLineChartTimeFromQuery(initialData?.query || '');
    if (!t) return;
    const g = (t.granularity || 'day').toLowerCase();
    setLineTimeGranularity(['year', 'month', 'day'].includes(g) ? g : 'day');
    setLineTimeScope(t.scope === 'filtered' ? 'filtered' : 'all');
    setLineFilterYear(t.filterYear != null ? String(t.filterYear) : '');
    setLineFilterMonth(t.filterMonth != null ? String(t.filterMonth) : '');
    setLineFilterYearStart(t.filterYearStart != null ? String(t.filterYearStart) : '');
    setLineFilterYearEnd(t.filterYearEnd != null ? String(t.filterYearEnd) : '');
  }, [open, lineMode, initialData?.query]);

  const columnKey = (c) => String(c).trim().toLowerCase();
  const findColumn = (list, name) =>
    list.find((c) => columnKey(c) === columnKey(name));

  const fetchChartData = async (isGrouped = false) => {
    if (!jobId) {
      setError('JobID not found. Please try again.');
      return;
    }

    setLoading(true);
    setError(null);
    setAnalyzeError(null);

    try {
      // Fetch all data for chart rendering (not just preview)
      const response = await api.get(`/preview/${jobId}?all=true`);
      
      if (response.data?.success) {
        const data = response.data.data || {};
        // Preview endpoint returns 'previewData' not 'rows'
        const previewRows = data.previewData || data.rows || [];
        setChartData(previewRows);
        const columnsList = data.columns?.map((col) => col.name || col) || [];
        setColumns(columnsList);

        let dateColNames = [];
        if (lineMode) {
          try {
            const ar = await api.get(`/preview/${jobId}?analyze=true`);
            if (ar.data?.success) {
              const raw = (ar.data.data?.columns || [])
                .filter((c) => (c.type || '').toLowerCase() === 'date')
                .map((c) => c.name);
              dateColNames = raw.filter((n) => findColumn(columnsList, n));
            }
          } catch (analyzeErr) {
            console.warn(analyzeErr);
            setAnalyzeError('Could not load column analysis. Use a column that contains dates for the X axis.');
          }
          dateColNames = mergeDateLikeColumnNames(columnsList, previewRows, getCellValue, dateColNames);
        }
        setDateColumns(dateColNames);

        const xPool = lineMode ? dateColNames : columnsList;

        // Preserve X/Y when a duplicate or slower preview response completes after the user changed axes.
        setSelectedXAxis((prev) => {
          if (!columnsList.length) return '';
          if (lineMode && !xPool.length) {
            if (prev && findColumn(columnsList, prev)) return findColumn(columnsList, prev);
            return '';
          }
          if (prev && findColumn(xPool, prev)) return findColumn(xPool, prev);
          
          if (isGrouped) {
            const fromInitial = initialData?.selectedColumns?.[0];
            if (fromInitial && findColumn(xPool, fromInitial)) return findColumn(xPool, fromInitial);
          }
          return xPool[0] || '';
        });
        // Series grouped bars store [category, series, value]; plain grouped store [category, value]
        const initialCols = initialData?.selectedColumns || [];
        const hasSeriesCols = isGrouped && initialCols.length === 3;
        if (hasSeriesCols) {
          const s = findColumn(columnsList, initialCols[1]);
          if (s) setSelectedSeries(s);
        }
        setSelectedYAxis((prev) => {
          if (!columnsList.length) return '';
          if (prev && findColumn(columnsList, prev)) return findColumn(columnsList, prev);

          const fromInitial = isGrouped
            ? (hasSeriesCols ? initialCols[2] : initialCols[1])
            : initialCols[0];
          if (fromInitial && findColumn(columnsList, fromInitial)) {
            return findColumn(columnsList, fromInitial);
          }
          if (columnsList.length >= 2) return columnsList[1];
          return columnsList[0] || '';
        });
      }
    } catch (error) {
      console.error('Error fetching chart data:', error);
      setError(getApiErrorMessage(error, 'Failed to load chart data. Please try again.'));
    } finally {
      setLoading(false);
    }
  };

  const computeMedian = (arr) => {
    if (arr.length === 0) return 0;
    const sorted = [...arr].sort((a, b) => a - b);
    const mid = Math.floor(sorted.length / 2);
    return sorted.length % 2 !== 0 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
  };

  const parseNum = (val) => {
    if (typeof val === 'number' && !isNaN(val)) return val;
    const s = String(val).replace(/,/g, '').trim();
    const n = parseFloat(s);
    return s !== '' && !isNaN(n) ? n : NaN;
  };

  const processDataForSimpleBarChart = () => {
    if (!selectedYAxis || chartData.length === 0) return;
    try {
      const colValues = chartData.map(row => getCellValue(row, selectedYAxis));
      const nonEmpty = colValues.filter(val => val !== null && val !== undefined && val !== '');
      const numValues = nonEmpty.map(parseNum).filter(n => !isNaN(n));
      const isNumerical = numValues.length > 0;

      if (isNumerical && aggregation !== 'count') {
        setDataType('numerical');
        const count = numValues.length;
        const sum = numValues.reduce((a, b) => a + b, 0);
        const agg = aggregation;
        let value = 0;
        switch (agg) {
          case 'sum': value = sum; break;
          case 'average': value = count > 0 ? sum / count : 0; break;
          case 'min': value = count > 0 ? Math.min(...numValues) : 0; break;
          case 'max': value = count > 0 ? Math.max(...numValues) : 0; break;
          case 'median': value = computeMedian(numValues); break;
          default: value = count > 0 ? sum / count : 0;
        }
        const aggLabel = AGGREGATION_OPTIONS.find(o => o.value === agg)?.label || agg;
        setProcessedData([{ name: aggLabel, value: typeof value === 'number' ? parseFloat(value.toFixed(2)) : value }]);
      } else {
        // Count is ALWAYS a distribution: one bar per distinct value (never a single total).
        // Numeric values (e.g. Rating 1-5) sort by value; text values sort by count.
        setDataType('count');
        const grouped = {};
        chartData.forEach(row => {
          const raw = getCellValue(row, selectedYAxis);
          if (raw === null || raw === undefined || raw === '') {
            grouped['(blank)'] = (grouped['(blank)'] || 0) + 1;
            return;
          }
          // Multi-select answers ("Coffee; Tea") count once per selected option
          splitMultiValue(raw).forEach((part) => {
            const key = part === '' ? '(blank)' : part;
            grouped[key] = (grouped[key] || 0) + 1;
          });
        });
        const processed = Object.entries(grouped).map(([name, value]) => ({ name, value }));
        if (isNumerical) {
          processed.sort((a, b) => {
            const na = parseNum(a.name);
            const nb = parseNum(b.name);
            const aNum = !isNaN(na);
            const bNum = !isNaN(nb);
            if (aNum && bNum) return na - nb;
            if (aNum) return -1;
            if (bNum) return 1;
            return a.name.localeCompare(b.name);
          });
        } else {
          processed.sort((a, b) => b.value - a.value);
        }
        setProcessedData(processed);
      }
    } catch (e) {
      console.error('Error processing simple bar chart data:', e);
      setError('Error processing data for chart.');
    }
  };

  const processLineChartData = () => {
    if (!selectedXAxis || !selectedYAxis || chartData.length === 0) {
      setProcessedData([]);
      return;
    }
    try {
      const timeCfg = {
        granularity: lineTimeGranularity,
        scope: lineTimeScope,
        filterYear:
          lineTimeScope === 'filtered' && ['day', 'month'].includes(lineTimeGranularity)
            ? (lineFilterYear === '' ? null : Number(lineFilterYear))
            : null,
        filterMonth:
          lineTimeScope === 'filtered' && lineTimeGranularity === 'day'
            ? (lineFilterMonth === '' ? null : Number(lineFilterMonth))
            : null,
        filterYearStart:
          lineTimeScope === 'filtered' && lineTimeGranularity === 'year'
            ? (lineFilterYearStart === '' ? null : Number(lineFilterYearStart))
            : null,
        filterYearEnd:
          lineTimeScope === 'filtered' && lineTimeGranularity === 'year'
            ? (lineFilterYearEnd === '' ? null : Number(lineFilterYearEnd))
            : null,
      };
      const data = aggregateLineChartPreview(
        chartData,
        getCellValue,
        selectedXAxis,
        selectedYAxis,
        aggregation,
        timeCfg
      );
      setDataType(isNumericalY ? 'numerical' : 'count');
      setProcessedData(data);
      setStackedSegmentKeys([]);
    } catch (e) {
      console.error(e);
      setError('Error building line chart data.');
    }
  };

  const processDataForChart = () => {
    setGroupedSeriesMode(false);
    if (chartTab === 'simple') {
      processDataForSimpleBarChart();
      return;
    }
    
    if (chartTab !== 'grouped') return;

    if (!selectedXAxis || !selectedYAxis || chartData.length === 0) {
      return;
    }

    try {
      if (lineMode) {
        processLineChartData();
        return;
      }
      if (isGroupedBothString) {
        setDataType('count');
        setStackedSegmentKeys([]);
        const categoryKey = selectedXAxis;
        const segmentKey = selectedYAxis;
        // Multi-select answers ("Coffee; Tea") count once per selected option
        const namesOf = (v) => (
          v === null || v === undefined || v === '' ? ['(blank)'] : splitMultiValue(v)
        );
        const distinctSegments = [...new Set(chartData.flatMap(row => namesOf(getCellValue(row, segmentKey))))];
        const byCategory = {};
        chartData.forEach(row => {
          const catNames = namesOf(getCellValue(row, categoryKey));
          const segNames = namesOf(getCellValue(row, segmentKey));
          catNames.forEach(catName => {
            if (!byCategory[catName]) {
              byCategory[catName] = { name: catName };
              distinctSegments.forEach(s => { byCategory[catName][s] = 0; });
            }
            segNames.forEach(segName => {
              byCategory[catName][segName] = (byCategory[catName][segName] || 0) + 1;
            });
          });
        });
        const processed = Object.values(byCategory).map(row => ({ ...row }));
        const total = (row) => distinctSegments.reduce((sum, s) => sum + (row[s] || 0), 0);
        processed.sort((a, b) => total(b) - total(a));
        const limitedData = processed.slice(0, 10);
        setProcessedData(limitedData);
        setStackedSegmentKeys(distinctSegments);
        return;
      }

      const yValues = chartData.map(row => getCellValue(row, selectedYAxis)).filter(val => val !== null && val !== undefined && val !== '');
      const numValues = yValues.map(parseNum).filter(n => !isNaN(n));
      const isNumerical = numValues.length > 0 && numValues.length === yValues.length;

      setStackedSegmentKeys([]);

      // Side-by-side grouped bars: Category × Series with an aggregated numeric value
      // (e.g. avg rating per facility, split by site)
      if (isNumerical && selectedSeries && selectedSeries !== selectedXAxis && selectedSeries !== selectedYAxis && findColumn(columns, selectedSeries)) {
        setDataType('numerical');
        setGroupedSeriesMode(true);
        const seriesNames = [];
        const seen = new Set();
        const groups = {};
        // Multi-select answers ("Coffee; Tea") count once per selected option
        const partsOf = (v) => (
          v === null || v === undefined || v === '' ? ['(blank)'] : splitMultiValue(v)
        );
        chartData.forEach(row => {
          const cats = partsOf(getCellValue(row, selectedXAxis));
          const segs = partsOf(getCellValue(row, selectedSeries));
          const yv = parseNum(getCellValue(row, selectedYAxis));
          if (isNaN(yv)) return;
          cats.forEach(cat => {
            segs.forEach(seg => {
              if (!seen.has(seg)) { seen.add(seg); seriesNames.push(seg); }
              if (!groups[cat]) groups[cat] = {};
              if (!groups[cat][seg]) groups[cat][seg] = { count: 0, sum: 0, values: [] };
              const g = groups[cat][seg];
              g.count += 1;
              g.sum += yv;
              g.values.push(yv);
            });
          });
        });
        seriesNames.sort((a, b) => a.localeCompare(b));
        const aggValue = (g) => {
          switch (aggregation) {
            case 'sum': return g.sum;
            case 'count': return g.count;
            case 'min': return g.values.length ? Math.min(...g.values) : 0;
            case 'max': return g.values.length ? Math.max(...g.values) : 0;
            case 'median': return computeMedian(g.values);
            case 'average':
            default: return g.count > 0 ? g.sum / g.count : 0;
          }
        };
        const processed = Object.entries(groups).map(([name, segMap]) => {
          const row = { name };
          seriesNames.forEach((s) => {
            const g = segMap[s];
            row[s] = g ? parseFloat(aggValue(g).toFixed(2)) : 0;
          });
          return row;
        });
        processed.sort((a, b) => a.name.localeCompare(b.name));
        setProcessedData(processed.slice(0, 10));
        setStackedSegmentKeys(seriesNames.slice(0, 10));
        return;
      }

      if (isNumerical) {
        setDataType('numerical');
        const groupedData = {};
        
        chartData.forEach(row => {
          const xRaw = getCellValue(row, selectedXAxis) || 'Unknown';
          const yValue = parseNum(getCellValue(row, selectedYAxis));

          if (isNaN(yValue)) return;

          // Multi-select answers ("Coffee; Tea") count once per selected option
          splitMultiValue(xRaw).forEach(xValue => {
            if (!groupedData[xValue]) {
              groupedData[xValue] = { count: 0, sum: 0, values: [] };
            }
            groupedData[xValue].count += 1;
            groupedData[xValue].sum += yValue;
            groupedData[xValue].values.push(yValue);
          });
        });

        const processed = Object.entries(groupedData).map(([name, data]) => {
          let value = 0;
          switch (aggregation) {
            case 'sum':
              value = data.sum;
              break;
            case 'count':
              value = data.count;
              break;
            case 'average':
              value = data.count > 0 ? data.sum / data.count : 0;
              break;
            case 'min':
              value = data.values.length > 0 ? Math.min(...data.values) : 0;
              break;
            case 'max':
              value = data.values.length > 0 ? Math.max(...data.values) : 0;
              break;
            case 'median':
              value = computeMedian(data.values);
              break;
            default:
              value = data.count > 0 ? data.sum / data.count : 0;
          }
          return {
            name,
            value: typeof value === 'number' ? parseFloat(value.toFixed(2)) : value
          };
        });

        processed.sort((a, b) => b.value - a.value);
        const limitedData = processed.slice(0, 10);
        setProcessedData(limitedData);
      } else {
        setDataType('count');
        const groupedData = {};
        
        chartData.forEach(row => {
          const xRaw = getCellValue(row, selectedXAxis) || 'Unknown';

          // Multi-select answers ("Coffee; Tea") count once per selected option
          splitMultiValue(xRaw).forEach(xValue => {
            if (!groupedData[xValue]) {
              groupedData[xValue] = 0;
            }
            groupedData[xValue] += 1;
          });
        });

        const processed = Object.entries(groupedData).map(([name, count]) => ({
          name: name,
          value: count
        }));

        processed.sort((a, b) => b.value - a.value);
        const limitedData = processed.slice(0, 10);
        setProcessedData(limitedData);
      }
    } catch (error) {
      console.error('Error processing data:', error);
      setError('Error processing data for chart.');
    }
  };

  useEffect(() => {
    if (chartTab === 'simple' && selectedYAxis && chartData.length > 0) {
      processDataForChart();
    } else if (chartTab === 'grouped' && selectedXAxis && selectedYAxis && chartData.length > 0) {
      processDataForChart();
    } 
  }, [
    chartTab,
    selectedXAxis,
    selectedYAxis,
    selectedSeries,
    chartData,
    aggregation,
    lineMode,
    lineTimeGranularity,
    lineTimeScope,
    lineFilterYear,
    lineFilterMonth,
    lineFilterYearStart,
    lineFilterYearEnd,
  ]);

  const handleSwapCategoryValue = () => {
    setSelectedXAxis(selectedYAxis);
    setSelectedYAxis(selectedXAxis);
  };

  const handleAddToDashboard = () => {
    if (lineMode) {
      setError(null);
      if (!selectedXAxis) {
        setError('Select a date column for the X axis.');
        return;
      }
      if (!dateColumns.length) {
        setError('No date-type columns are available for the X axis. Use the Data Analyzer to confirm types, or ensure a date column uses a consistent format.');
        return;
      }
      if (lineTimeScope === 'filtered') {
        if (lineTimeGranularity === 'day' && (!lineFilterYear || !lineFilterMonth)) {
          setError('Select a year and month to show daily values for that month.');
          return;
        }
        if (lineTimeGranularity === 'month' && !lineFilterYear) {
          setError('Select a year to show monthly values for that year.');
          return;
        }
        if (lineTimeGranularity === 'year' && (!lineFilterYearStart || !lineFilterYearEnd)) {
          setError('Select a start year and end year for the filtered range.');
          return;
        }
        if (lineTimeGranularity === 'year' && Number(lineFilterYearStart) > Number(lineFilterYearEnd)) {
          setError('Start year must be before or equal to end year.');
          return;
        }
      }
    }
    if (processedData.length > 0 && widgetName.trim()) {
      const lineTimeDesc = lineMode
        ? ` — ${lineTimeGranularity} buckets, ${lineTimeScope === 'all' ? 'all data' : 'filtered period'}`
        : '';
      const seriesActive = chartTab === 'grouped' && !lineMode && !isGroupedBothString && isNumericalY && !!selectedSeries && selectedSeries !== selectedXAxis && selectedSeries !== selectedYAxis;
      const aggLabel = AGGREGATION_OPTIONS.find(o => o.value === aggregation)?.label || aggregation;
      const description = chartTab === 'simple'
        ? (aggregation === 'count'
            ? (isNumericalY ? `Shows ${selectedYAxis} — distribution (count per value)` : `Shows ${selectedYAxis} (unique values vs count)`)
            : `Shows ${selectedYAxis} - ${aggLabel}`)
        : chartTab === 'grouped'
          ? (lineMode
              ? `Trend by ${selectedXAxis}: ${aggLabel} of ${selectedYAxis}${lineTimeDesc}`
              : (isGroupedBothString
                  ? `Stacked: Category ${selectedXAxis}, segments by ${selectedYAxis}`
                  : seriesActive
                    ? `Category: ${selectedXAxis} × ${selectedSeries}, ${aggLabel} of ${selectedYAxis}`
                    : `Category: ${selectedXAxis}, ${aggLabel} of ${selectedYAxis}`))
          : `Shows top ${processedData.length} categories by ${selectedYAxis} value`;
      const cardType = lineMode
        ? 'Build Line Chart'
        : (chartTab === 'simple' ? 'Simple Bar Chart' : chartTab === 'grouped' ? 'Grouped Bar Chart' : 'Build Bar Chart');
      const selectedCols = chartTab === 'simple'
        ? [selectedYAxis]
        : seriesActive
          ? [selectedXAxis, selectedSeries, selectedYAxis]
          : [selectedXAxis, selectedYAxis];
      const lineQuery = lineMode
        ? mergeLineChartTimeIntoQuery('', {
            granularity: lineTimeGranularity,
            scope: lineTimeScope,
            filterYear:
              lineTimeScope === 'filtered' && ['day', 'month'].includes(lineTimeGranularity)
                ? (lineFilterYear === '' ? null : Number(lineFilterYear))
                : null,
            filterMonth:
              lineTimeScope === 'filtered' && lineTimeGranularity === 'day'
                ? (lineFilterMonth === '' ? null : Number(lineFilterMonth))
                : null,
            filterYearStart:
              lineTimeScope === 'filtered' && lineTimeGranularity === 'year'
                ? (lineFilterYearStart === '' ? null : Number(lineFilterYearStart))
                : null,
            filterYearEnd:
              lineTimeScope === 'filtered' && lineTimeGranularity === 'year'
                ? (lineFilterYearEnd === '' ? null : Number(lineFilterYearEnd))
                : null,
          })
        : '';
      const widgetData = [{
        recommendation: widgetName.trim() || `${chartDisplayName}: ${selectedYAxis}`,
        notWorkingWell: description,
        widgetName: widgetName.trim(),
        widgetType: lineMode ? 'line_chart' : 'bar_chart',
        cardType,
        selectedColumns: selectedCols,
        aggregation: aggregation || null,
        // Dashboard SQL: single-column + count is always GROUP BY (distribution), never one total
        query:
          lineMode
            ? lineQuery
            : chartTab === 'simple' && !isNumericalY
              ? JSON.stringify({ __simpleBar: 'categorical', showValuesOnBars })
              : chartTab === 'simple' && aggregation === 'count'
                ? JSON.stringify({ __simpleBar: 'distribution', showValuesOnBars })
                : chartTab === 'grouped' && isGroupedBothString && !lineMode
                  ? JSON.stringify({ __groupedBar: 'stacked_categorical', showValuesOnBars })
                  : seriesActive
                    ? JSON.stringify({ __groupedBar: 'series', showValuesOnBars })
                    : JSON.stringify({ showValuesOnBars }),
      }];
      
      onAddToDashboard(widgetData);
      onClose();
    }
  };

  const renderChart = () => {
    if (processedData.length === 0) {
      return (
        <Box className="bar-chart-empty">
          <Typography variant="caption" color="text.secondary">
            {chartTab === 'simple' ? 'Select column and aggregation to generate chart' : chartTab === 'grouped' ? 'Select Category and Value column to generate chart' : 'Select X and Y axes to generate chart'}
          </Typography>
        </Box>
      );
    }

    // Single numeric value (e.g. Average/Sum of one column): show summary card + slim bar instead of one full-width block
    const isSingleNumericBar = chartTab === 'simple' && dataType === 'numerical' && processedData.length === 1;
    if (isSingleNumericBar) {
      const single = processedData[0];
      const aggLabel = single?.name || AGGREGATION_OPTIONS.find(o => o.value === aggregation)?.label || aggregation;
      const value = single?.value ?? 0;
      const displayValue = typeof value === 'number' ? (Number.isInteger(value) ? value : value.toFixed(2)) : value;
      return (
        <Box className="build-bar-chart-single-value">
          <Box className="build-bar-chart-single-value-card">
            <Typography className="build-bar-chart-single-value-label" variant="body2" color="text.secondary">
              {aggLabel} of &quot;{selectedYAxis}&quot;
            </Typography>
            <Typography className="build-bar-chart-single-value-number" component="span" variant="h4">
              {displayValue}
            </Typography>
          </Box>
        </Box>
      );
    }

    const isHorizontal = !lineMode && chartTab === 'simple' && dataType === 'count';
    if (lineMode) {
      return (
        <ResponsiveContainer width="100%" height={350}>
          <LineChart data={processedData} margin={{ top: 0, right: 5, left: 0, bottom: 80 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
              dataKey="name"
              angle={-45}
              textAnchor="end"
              height={60}
              interval={0}
              fontSize={9}
              tickFormatter={(value) => (typeof value === 'string' && value.length > 12 ? value.substring(0, 8) + '...' : value)}
            />
            <YAxis
              domain={[0, 'dataMax']}
              allowDecimals={dataType === 'numerical'}
              tickFormatter={(value) => {
                if (typeof value !== 'number') return value;
                if (dataType === 'numerical') return value.toFixed(2);
                return Math.round(value).toString();
              }}
              fontSize={10}
              width={40}
            />
            <RechartsTooltip
              formatter={(value, name) => {
                const formattedValue = typeof value === 'number' ? value.toFixed(2) : value;
                return [formattedValue, name === 'value' ? (AGGREGATION_OPTIONS.find(o => o.value === aggregation)?.label || aggregation) : name];
              }}
              labelFormatter={(label) => `${selectedXAxis || 'Category'}: ${label}`}
            />
            <Legend iconSize={8} />
            <Line
              type="monotone"
              dataKey="value"
              stroke={chartColors[0]}
              strokeWidth={2}
              dot={{ r: 3 }}
              activeDot={{ r: 4 }}
              name={selectedYAxis || 'Value'}
            />
          </LineChart>
        </ResponsiveContainer>
      );
    }

    return (
      <ResponsiveContainer width="100%" height={isHorizontal ? Math.max(350, processedData.length * 24) : 350}>
        <BarChart
          data={processedData}
          layout={isHorizontal ? 'vertical' : 'horizontal'}
          margin={isHorizontal ? { top: 5, right: 30, left: 100, bottom: 5 } : { top: 0, right: 5, left: 0, bottom: 80 }}
        >
          <CartesianGrid strokeDasharray="3 3" />
          {isHorizontal ? (
            <>
              <XAxis type="number" domain={[0, 'dataMax']} allowDecimals={dataType === 'numerical'} fontSize={10} tickFormatter={(v) => typeof v === 'number' ? (dataType === 'numerical' ? v.toFixed(2) : Math.round(v).toString()) : v} />
              <YAxis type="category" dataKey="name" width={95} fontSize={9} tickFormatter={(v) => (typeof v === 'string' && v.length > 14 ? v.substring(0, 12) + '...' : v)} />
            </>
          ) : (
            <>
              <XAxis dataKey="name" angle={-45} textAnchor="end" height={60} interval={0} fontSize={9} tickFormatter={(value) => (typeof value === 'string' && value.length > 12 ? value.substring(0, 8) + '...' : value)} />
              <YAxis domain={[0, 'dataMax']} allowDecimals={dataType === 'numerical'} tickFormatter={(value) => { if (typeof value !== 'number') return value; if (dataType === 'numerical') return value.toFixed(2); return Math.round(value).toString(); }} fontSize={10} width={40} />
            </>
          )}
          {!showValuesOnBars && (
            <RechartsTooltip 
              formatter={(value, name) => {
                const formattedValue = typeof value === 'number' ? value.toFixed(2) : value;
                return [formattedValue, name === 'value' ? (AGGREGATION_OPTIONS.find(o => o.value === aggregation)?.label || aggregation) : name];
              }}
              labelFormatter={(label) => chartTab === 'simple' ? `${selectedYAxis}: ${label}` : `${selectedXAxis || 'Category'}: ${label}`}
            />
          )}
          <Legend 
            wrapperStyle={{ 
              fontSize: '0.5625rem', 
              paddingTop: '15px', 
              marginBottom: '0px', 
              marginTop: '0px',
              maxHeight: '2.5rem',
              overflow: 'hidden'
            }} 
            iconSize={8}
            formatter={(value) => {
              if (typeof value === 'string' && value.length > 12) {
                return value.substring(0, 9) + '...';
              }
              return value;
            }}
          />
          {(isGroupedBothString || groupedSeriesMode) && stackedSegmentKeys.length > 0 ? (
            stackedSegmentKeys.map((key, i) => (
              <Bar
                key={key}
                dataKey={key}
                stackId={groupedSeriesMode ? undefined : 'stack1'}
                fill={chartColors[i % chartColors.length]}
                name={key}
                label={showValuesOnBars ? { position: isHorizontal ? 'right' : 'top', fill: '#000', fontSize: 10, formatter: (val) => typeof val === 'number' ? (Number.isInteger(val) ? val : val.toFixed(2)) : val } : false}
              />
            ))
          ) : (
            <Bar 
              dataKey="value" 
              fill={chartColors[0]} 
              name={chartTab === 'simple' ? (AGGREGATION_OPTIONS.find(o => o.value === aggregation)?.label || aggregation) : selectedYAxis} 
              label={showValuesOnBars ? { position: isHorizontal ? 'right' : 'top', fill: '#000', fontSize: 10, formatter: (val) => typeof val === 'number' ? (Number.isInteger(val) ? val : val.toFixed(2)) : val } : false}
            />
          )}
        </BarChart>
      </ResponsiveContainer>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={branding.dialog?.PaperProps}
      slotProps={branding.dialog?.slotProps}
      className="build-bar-chart-dialog"
    >
      <DialogTitle className="build-bar-chart-title">
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
          <Box className="build-bar-chart-title-content">
            <BarChartIcon className="build-bar-chart-icon" />
            <Typography variant="h6" component="span">{lineMode ? 'Build Line Chart' : 'Build Bar Chart'}</Typography>
          </Box>
          <IconButton aria-label="close" onClick={onClose} size="small" sx={{ ml: 1 }}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent className="build-bar-chart-content">
        {loading && (
          <Box className="build-bar-chart-loading">
            <CircularProgress />
          </Box>
        )}


        {!loading && !error && (
          <>
            {!lineMode && (
              <Tabs value={chartTab} onChange={(_, v) => setChartTab(v)} sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
                <Tab
                  value="simple"
                  icon={<BarChartIcon />}
                  iconPosition="start"
                  label={
                    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                      <span>Simple Bar Chart</span>
                      <Tooltip
                        title={
                          <Box component="span" sx={{ display: 'block', maxWidth: '20rem' }}>
                            <strong>Simple Bar Chart</strong> — Summarizes one column. <strong>Count:</strong> always a distribution — one bar per unique value (e.g. ratings 1–5 with how many chose each). <strong>Numeric + Sum/Average/Min/Max/Median:</strong> a single summary bar. Best for quick summaries and distributions.
                          </Box>
                        }
                        placement="top"
                        arrow
                        enterDelay={200}
                      >
                        <IconButton
                          size="small"
                          sx={{ p: 0.25, ml: 0.25 }}
                          onClick={(e) => e.stopPropagation()}
                          onMouseDown={(e) => e.stopPropagation()}
                          aria-label="Info about Simple Bar Chart"
                        >
                          <InfoOutlinedIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  }
                />
                <Tab
                  value="grouped"
                  icon={<BarChartIcon />}
                  iconPosition="start"
                  label={
                    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                      <span>Grouped Bar Chart</span>
                      <Tooltip
                        title={
                          <Box component="span" sx={{ display: 'block', maxWidth: '20rem' }}>
                            <strong>Grouped Bar Chart</strong> — Compares values across categories. Pick a <strong>Category</strong> (X-axis) and a <strong>Value</strong> column; optionally <strong>Split bars by</strong> a second category for side-by-side groups (e.g. average rating per facility, split by site). Text values give a stacked bar by count.
                          </Box>
                        }
                        placement="top"
                        arrow
                        enterDelay={200}
                      >
                        <IconButton
                          size="small"
                          sx={{ p: 0.25, ml: 0.25 }}
                          onClick={(e) => e.stopPropagation()}
                          onMouseDown={(e) => e.stopPropagation()}
                          aria-label="Info about Grouped Bar Chart"
                        >
                          <InfoOutlinedIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  }
                />
              </Tabs>
            )}

            {!lineMode && chartTab === 'simple' && (
              <>
                <Grid container spacing={2} className="build-bar-chart-inputs" sx={{ alignItems: 'flex-start' }}>
                  <Grid item xs={12} md={4} sx={{ display: 'flex', flexDirection: 'column' }}>
                    <Box sx={{ mb: 1, minHeight: '1.25rem' }}>
                      <Typography
                        variant="subtitle2"
                        sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#666666', fontFamily: branding.fonts.primary }}
                      >
                        Card Name
                        <Box component="span" sx={{ color: '#666666', ml: 0.25 }}>*</Box>:
                      </Typography>
                    </Box>
                    <TextField
                      fullWidth
                      value={widgetName}
                      onChange={(e) => setWidgetName(e.target.value)}
                      placeholder={`Enter a name for this ${lineMode ? 'line' : 'bar'} chart card`}
                      variant="outlined"
                      required
                      helperText="Give your card a descriptive name"
                      sx={{ mt: 0 }}
                      inputProps={{ 'aria-label': 'Card Name' }}
                      InputProps={{
                        startAdornment: <BarChartIcon sx={{ mr: 1, color: '#000000', fontSize: '1.25rem' }} />
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={4} sx={{ display: 'flex', flexDirection: 'column' }}>
                    <Box sx={{ mb: 1, minHeight: '1.25rem' }}>
                      <Typography variant="subtitle2" sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#666666' }}>
                        Column (Y-axis = unique values):
                      </Typography>
                    </Box>
                    <FormControl fullWidth sx={{ mt: 0 }}>
                      <InputLabel>Column</InputLabel>
                      <Select
                        value={selectedYAxis}
                        onChange={handleYAxisChange}
                        label="Column"
                        MenuProps={{ PaperProps: branding.menu?.PaperProps }}
                      >
                        {columns.map((column) => (
                          <MenuItem key={column} value={column}>
                            {column}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={4} sx={{ display: 'flex', flexDirection: 'column' }}>
                    <Box sx={{ mb: 1, minHeight: '1.25rem' }}>
                      <Typography variant="subtitle2" sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#666666' }}>
                        Aggregation (X-axis):
                      </Typography>
                    </Box>
                    <FormControl fullWidth sx={{ mt: 0 }}>
                      <InputLabel>Aggregation</InputLabel>
                      <Select
                        value={aggregation}
                        onChange={(e) => setAggregation(e.target.value)}
                        label="Aggregation"
                        MenuProps={{ PaperProps: branding.menu?.PaperProps }}
                      >
                        {isNumericalY
                          ? AGGREGATION_OPTIONS.map((opt) => (
                              <MenuItem key={opt.value} value={opt.value}>
                                {opt.label}
                              </MenuItem>
                            ))
                          : (
                              <MenuItem value="count">Count</MenuItem>
                            )}
                      </Select>
                      {!isNumericalY && (
                        <Typography component="span" variant="caption" sx={{ display: 'block', mt: 0.5, fontSize: '0.7rem', fontStyle: 'italic', color: 'text.secondary' }}>
                          For textual columns only Count can be used.
                        </Typography>
                      )}
                    </FormControl>
                  </Grid>
                </Grid>
                <Box sx={{ mt: 1 }} />
                <Typography variant="caption" color="text.secondary">
                  {isNumericalY
                    ? 'Count: one bar per unique value (distribution). Sum / Average / Min / Max / Median: a single summary bar.'
                    : <>Y-axis shows unique values of the selected column. X-axis shows count. <Box component="span" sx={{ fontSize: '0.7rem', fontStyle: 'italic' }}>For textual columns only Count can be used.</Box></>}
                </Typography>

                <Box className="build-bar-chart-preview" sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box>
                    <Typography variant="h6" gutterBottom>
                      Chart Preview
                    </Typography>
                    <Typography variant="caption" color="text.secondary" gutterBottom>
                      {selectedYAxis
                        ? (isNumericalY
                            ? (aggregation === 'count'
                                ? `Distribution of "${selectedYAxis}": one bar per value with its count.`
                                : `Showing ${AGGREGATION_OPTIONS.find(o => o.value === aggregation)?.label || aggregation} of "${selectedYAxis}".`)
                            : <>Y-axis: unique values of &quot;{selectedYAxis}&quot;. X-axis: count. <Box component="span" sx={{ fontSize: '0.7rem', fontStyle: 'italic' }}>For textual columns only Count can be used.</Box></>)
                        : 'Select column and aggregation to generate chart'}
                    </Typography>
                  </Box>
                  {!lineMode && (
                    <FormControlLabel
                      control={
                        <Switch
                          checked={showValuesOnBars}
                          onChange={(e) => setShowValuesOnBars(e.target.checked)}
                          color="primary"
                          size="small"
                        />
                      }
                      label={<Typography variant="body2">Show values on bars</Typography>}
                    />
                  )}
                </Box>

                {renderChart()}
              </>
            )}

            {chartTab === 'grouped' && (
              <>
                <Grid container spacing={2} className="build-bar-chart-inputs" sx={{ alignItems: 'flex-start' }}>
                  <Grid item xs={12} md={4} sx={{ display: 'flex', flexDirection: 'column' }}>
                    <Box sx={{ mb: 1, minHeight: '1.25rem' }}>
                      <Typography
                        variant="subtitle2"
                        sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#666666', fontFamily: branding.fonts.primary }}
                      >
                        Card Name
                        <Box component="span" sx={{ color: '#666666', ml: 0.25 }}>*</Box>:
                      </Typography>
                    </Box>
                    <TextField
                      fullWidth
                      value={widgetName}
                      onChange={(e) => setWidgetName(e.target.value)}
                      placeholder={`Enter a name for this ${lineMode ? 'line' : 'bar'} chart card`}
                      variant="outlined"
                      required
                      helperText="Give your card a descriptive name"
                      sx={{ mt: 0 }}
                      inputProps={{ 'aria-label': 'Card Name' }}
                      InputProps={{
                        startAdornment: <BarChartIcon sx={{ mr: 1, color: '#000000', fontSize: '1.25rem' }} />
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={3} sx={{ display: 'flex', flexDirection: 'column' }}>
                    <Box sx={{ mb: 1, minHeight: '1.25rem' }}>
                      <Typography variant="subtitle2" sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#666666' }}>
                        {lineMode ? 'Date column (X axis)' : 'Category (group by)'}
                      </Typography>
                    </Box>
                    <FormControl fullWidth sx={{ mt: 0 }}>
                      <InputLabel>{lineMode ? 'Date column' : 'Category (group by)'}</InputLabel>
                      <Select
                        value={selectedXAxis}
                        onChange={(e) => setSelectedXAxis(e.target.value)}
                        label={lineMode ? 'Date column' : 'Category (group by)'}
                        MenuProps={{ PaperProps: branding.menu?.PaperProps }}
                      >
                        {(lineMode ? dateColumns : columns).map((column) => (
                          <MenuItem key={column} value={column}>
                            {column}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={1} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <IconButton
                      onClick={handleSwapCategoryValue}
                      disabled={lineMode}
                      aria-label="Swap Category and Value column"
                      title={lineMode ? 'Swap is disabled for line charts (X must stay a date)' : 'Swap Category and Value column'}
                      size="medium"
                      sx={{ color: 'primary.main' }}
                    >
                      <SwapHorizIcon />
                    </IconButton>
                  </Grid>
                  <Grid item xs={12} md={3} sx={{ display: 'flex', flexDirection: 'column' }}>
                    <Box sx={{ mb: 1, minHeight: '1.25rem' }}>
                      <Typography variant="subtitle2" sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#666666' }}>
                        Value column:
                      </Typography>
                    </Box>
                    <FormControl fullWidth sx={{ mt: 0 }}>
                      <InputLabel>Value column</InputLabel>
                      <Select
                        value={selectedYAxis}
                        onChange={handleYAxisChange}
                        label="Value column"
                        MenuProps={{ PaperProps: branding.menu?.PaperProps }}
                      >
                        {columns.map((column) => (
                          <MenuItem key={column} value={column}>
                            {column}
                          </MenuItem>   
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={4} sx={{ display: 'flex', flexDirection: 'column' }}>
                    <Box sx={{ mb: 1, minHeight: '1.25rem' }}>
                      <Typography variant="subtitle2" sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#666666' }}>
                        Aggregation:
                      </Typography>
                    </Box>
                    <FormControl fullWidth sx={{ mt: 0 }}>
                      <InputLabel>Aggregation</InputLabel>
                      <Select
                        value={aggregation}
                        onChange={(e) => setAggregation(e.target.value)}
                        label="Aggregation"
                        MenuProps={{ PaperProps: branding.menu?.PaperProps }}
                      >
                        {isNumericalY
                          ? AGGREGATION_OPTIONS.map((opt) => (
                              <MenuItem key={opt.value} value={opt.value}>
                                {opt.label}
                              </MenuItem>
                            ))
                          : (
                              <MenuItem value="count">Count</MenuItem>
                            )}
                      </Select>
                      {!isNumericalY && (
                        <Typography component="span" variant="caption" sx={{ display: 'block', mt: 0.5, fontSize: '0.7rem', fontStyle: 'italic', color: 'text.secondary' }}>
                          For textual columns only Count can be used.
                        </Typography>
                      )}
                    </FormControl>
                  </Grid>
                  {!lineMode && isNumericalY && (
                    <Grid item xs={12} md={4} sx={{ display: 'flex', flexDirection: 'column' }}>
                      <Box sx={{ mb: 1, minHeight: '1.25rem' }}>
                        <Typography variant="subtitle2" sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#666666' }}>
                          Split bars by (optional):
                        </Typography>
                      </Box>
                      <FormControl fullWidth sx={{ mt: 0 }}>
                        <InputLabel>Split bars by</InputLabel>
                        <Select
                          value={selectedSeries}
                          onChange={(e) => setSelectedSeries(e.target.value)}
                          label="Split bars by"
                          MenuProps={{ PaperProps: branding.menu?.PaperProps }}
                        >
                          <MenuItem value=""><em>None</em></MenuItem>
                          {columns
                            .filter((c) => c !== selectedXAxis && c !== selectedYAxis)
                            .map((column) => (
                              <MenuItem key={column} value={column}>
                                {column}
                              </MenuItem>
                            ))}
                        </Select>
                        <Typography component="span" variant="caption" sx={{ display: 'block', mt: 0.5, fontSize: '0.7rem', color: 'text.secondary' }}>
                          Side-by-side bars per category (e.g. facility × site).
                        </Typography>
                      </FormControl>
                    </Grid>
                  )}
                  {lineMode && (
                    <>
                      <Grid item xs={12}>
                        {analyzeError && (
                          <Alert severity="warning" sx={{ mb: 1 }}>{analyzeError}</Alert>
                        )}
                        {!loading && !dateColumns.length && (
                          <Alert severity="info" sx={{ mb: 1 }}>
                            No columns were detected as <strong>date</strong> type. Use the Data Analyzer on the preview page, or ensure a column has parseable dates (e.g. YYYY-MM-DD).
                          </Alert>
                        )}
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <FormControl fullWidth sx={{ mt: 0 }}>
                          <InputLabel>Time bucket</InputLabel>
                          <Select
                            value={lineTimeGranularity}
                            label="Time bucket"
                            onChange={(e) => setLineTimeGranularity(e.target.value)}
                            MenuProps={{ PaperProps: branding.menu?.PaperProps }}
                          >
                            <MenuItem value="day">By day</MenuItem>
                            <MenuItem value="month">By month</MenuItem>
                            <MenuItem value="year">By year</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} md={8}>
                        <Typography variant="subtitle2" sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#666666', mb: 0.5 }}>
                          Time range
                        </Typography>
                        <RadioGroup
                          row
                          value={lineTimeScope}
                          onChange={(e) => setLineTimeScope(e.target.value)}
                        >
                          <FormControlLabel value="all" control={<Radio size="small" />} label="All data" />
                          <FormControlLabel value="filtered" control={<Radio size="small" />} label="Selected period" />
                        </RadioGroup>
                      </Grid>
                      {lineTimeScope === 'filtered' && lineTimeGranularity === 'day' && (
                        <>
                          <Grid item xs={12} md={3}>
                            <FormControl fullWidth sx={{ mt: 0 }}>
                              <InputLabel>Year</InputLabel>
                              <Select
                                value={lineFilterYear}
                                label="Year"
                                onChange={(e) => setLineFilterYear(e.target.value)}
                                MenuProps={{ PaperProps: branding.menu?.PaperProps }}
                              >
                                <MenuItem value=""><em>Select year</em></MenuItem>
                                {availableYears.map((y) => (
                                  <MenuItem key={y} value={String(y)}>{y}</MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </Grid>
                          <Grid item xs={12} md={3}>
                            <FormControl fullWidth sx={{ mt: 0 }}>
                              <InputLabel>Month</InputLabel>
                              <Select
                                value={lineFilterMonth}
                                label="Month"
                                onChange={(e) => setLineFilterMonth(e.target.value)}
                                MenuProps={{ PaperProps: branding.menu?.PaperProps }}
                              >
                                <MenuItem value=""><em>Select month</em></MenuItem>
                                {monthMenuItems().map((m) => (
                                  <MenuItem key={m.value} value={String(m.value)}>{m.label}</MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </Grid>
                        </>
                      )}
                      {lineTimeScope === 'filtered' && lineTimeGranularity === 'month' && (
                        <Grid item xs={12} md={3}>
                          <FormControl fullWidth sx={{ mt: 0 }}>
                            <InputLabel>Year</InputLabel>
                            <Select
                              value={lineFilterYear}
                              label="Year"
                              onChange={(e) => setLineFilterYear(e.target.value)}
                              MenuProps={{ PaperProps: branding.menu?.PaperProps }}
                            >
                              <MenuItem value=""><em>Select year</em></MenuItem>
                              {availableYears.map((y) => (
                                <MenuItem key={y} value={String(y)}>{y}</MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Grid>
                      )}
                      {lineTimeScope === 'filtered' && lineTimeGranularity === 'year' && (
                        <>
                          <Grid item xs={12} md={3}>
                            <FormControl fullWidth sx={{ mt: 0 }}>
                              <InputLabel>From year</InputLabel>
                              <Select
                                value={lineFilterYearStart}
                                label="From year"
                                onChange={(e) => setLineFilterYearStart(e.target.value)}
                                MenuProps={{ PaperProps: branding.menu?.PaperProps }}
                              >
                                <MenuItem value=""><em>From</em></MenuItem>
                                {availableYears.map((y) => (
                                  <MenuItem key={y} value={String(y)}>{y}</MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </Grid>
                          <Grid item xs={12} md={3}>
                            <FormControl fullWidth sx={{ mt: 0 }}>
                              <InputLabel>To year</InputLabel>
                              <Select
                                value={lineFilterYearEnd}
                                label="To year"
                                onChange={(e) => setLineFilterYearEnd(e.target.value)}
                                MenuProps={{ PaperProps: branding.menu?.PaperProps }}
                              >
                                <MenuItem value=""><em>To</em></MenuItem>
                                {availableYears.map((y) => (
                                  <MenuItem key={y} value={String(y)}>{y}</MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </Grid>
                        </>
                      )}
                    </>
                  )}
                </Grid>
                <Box className="build-bar-chart-preview" sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box>
                    <Typography variant="h6" gutterBottom>
                      Chart Preview
                    </Typography>
                    <Typography variant="caption" color="text.secondary" gutterBottom>
                      {selectedXAxis && selectedYAxis
                        ? (lineMode
                            ? <>Trend by {lineTimeGranularity}: X &quot;{selectedXAxis}&quot;, Y {AGGREGATION_OPTIONS.find(o => o.value === aggregation)?.label || aggregation} of &quot;{selectedYAxis}&quot;{lineTimeScope === 'filtered' ? ' (selected period)' : ''}.</>
                            : isGroupedBothString
                            ? <>Stacked bar: Category &quot;{selectedXAxis}&quot; with segments by &quot;{selectedYAxis}&quot;. <Box component="span" sx={{ fontSize: '0.7rem', fontStyle: 'italic' }}>For textual columns only Count can be used.</Box></>
                            : <>Category: {selectedXAxis}{groupedSeriesMode && selectedSeries ? <> × {selectedSeries} (side-by-side bars)</> : null}. Value: {AGGREGATION_OPTIONS.find(o => o.value === aggregation)?.label || aggregation} of {selectedYAxis}.{!isNumericalY && <Box component="span" sx={{ fontSize: '0.7rem', fontStyle: 'italic' }}> For textual columns only Count can be used.</Box>}</>)
                        : `Select ${lineMode ? 'X and Y columns' : 'Category and Value column'} to generate chart`}
                    </Typography>
                  </Box>
                  {!lineMode && (
                    <FormControlLabel
                      control={
                        <Switch
                          checked={showValuesOnBars}
                          onChange={(e) => setShowValuesOnBars(e.target.checked)}
                          color="primary"
                          size="small"
                        />
                      }
                      label={<Typography variant="body2">Show values on bars</Typography>}
                    />
                  )}
                </Box>
                {renderChart()}
              </>
            )}

          </>
        )}
      </DialogContent>
      <DialogActions className="build-bar-chart-actions">
        <Button variant={branding.button.variant} size={branding.button.size} sx={branding.button.sx} onClick={onClose}>
          Cancel
        </Button>
        {editWidgetId && onUpdateCard ? (
          <Button
            variant={branding.button.variant}
            size={branding.button.size}
            sx={branding.button.sx}
            onClick={() => {
              const seriesActive = chartTab === 'grouped' && !lineMode && !isGroupedBothString && isNumericalY && !!selectedSeries && selectedSeries !== selectedXAxis && selectedSeries !== selectedYAxis;
              const selectedCols = chartTab === 'simple'
                ? [selectedYAxis]
                : (seriesActive
                    ? [selectedXAxis, selectedSeries, selectedYAxis]
                    : [selectedXAxis, selectedYAxis]).filter(Boolean);
              const lineQuery = lineMode
                ? mergeLineChartTimeIntoQuery(initialData?.query || '', {
                    granularity: lineTimeGranularity,
                    scope: lineTimeScope,
                    filterYear:
                      lineTimeScope === 'filtered' && ['day', 'month'].includes(lineTimeGranularity)
                        ? (lineFilterYear === '' ? null : Number(lineFilterYear))
                        : null,
                    filterMonth:
                      lineTimeScope === 'filtered' && lineTimeGranularity === 'day'
                        ? (lineFilterMonth === '' ? null : Number(lineFilterMonth))
                        : null,
                    filterYearStart:
                      lineTimeScope === 'filtered' && lineTimeGranularity === 'year'
                        ? (lineFilterYearStart === '' ? null : Number(lineFilterYearStart))
                        : null,
                    filterYearEnd:
                      lineTimeScope === 'filtered' && lineTimeGranularity === 'year'
                        ? (lineFilterYearEnd === '' ? null : Number(lineFilterYearEnd))
                        : null,
                  })
                : null;
              let barQueryObj = {};
              if (!lineMode) {
                if (chartTab === 'simple' && !isNumericalY) barQueryObj.__simpleBar = 'categorical';
                else if (chartTab === 'simple' && aggregation === 'count') barQueryObj.__simpleBar = 'distribution';
                if (chartTab === 'grouped' && isGroupedBothString) barQueryObj.__groupedBar = 'stacked_categorical';
                else if (seriesActive) barQueryObj.__groupedBar = 'series';
                barQueryObj.showValuesOnBars = showValuesOnBars;
              }

              const description = chartTab === 'simple'
                ? `Aggregation: ${aggregation || 'Count'}`
                : seriesActive
                  ? `Category: ${selectedXAxis} × ${selectedSeries}, Value: ${selectedYAxis}, Aggregation: ${aggregation || 'Count'}`
                  : `Category: ${selectedXAxis}, Value: ${selectedYAxis}, Aggregation: ${aggregation || 'Count'}`;

              onUpdateCard({
                widgetName: widgetName.trim(),
                selectedColumns: selectedCols,
                aggregation,
                widgetType,
                description,
                ...(lineMode ? (lineQuery ? { query: lineQuery } : {}) : { query: JSON.stringify(barQueryObj) }),
              });
            }}
            disabled={!widgetName.trim() || (chartTab === 'simple' ? !selectedYAxis : !selectedXAxis || !selectedYAxis)}
          >
            Update card
          </Button>
        ) : (
          <Button 
            variant={branding.button.variant}
            size={branding.button.size}
            sx={branding.button.sx}
            onClick={handleAddToDashboard}
            disabled={processedData.length === 0 || !widgetName.trim() || (chartTab === 'grouped' && (!selectedXAxis || !selectedYAxis))}
          >
            {initialData ? 'Edit Card' : 'Add Card'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default BuildBarChart;
